import { useState, useEffect, useRef } from 'react';
import { apiPost } from '../services/apiClient';

/**
 * Custom hook for fetching dimension dashboard data.
 * Manages two parallel fetches with request-ID tracking to discard stale responses.
 * Returns per-source loading/error states for progressive rendering.
 */
export default function useDimensionData({ topicId, dimension }) {
    const [npsData, setNpsData] = useState([]);
    const [quarterlyData, setQuarterlyData] = useState([]);
    const [loading, setLoading] = useState({ nps: false, quarterly: false });
    const [errors, setErrors] = useState({ nps: null, quarterly: null });
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!topicId || !dimension) {
            setNpsData([]);
            setQuarterlyData([]);
            setLoading({ nps: false, quarterly: false });
            setErrors({ nps: null, quarterly: null });
            return;
        }

        const reqId = ++requestIdRef.current;

        setLoading({ nps: true, quarterly: true });
        setErrors({ nps: null, quarterly: null });

        // Fetch NPS data (all-time, no period filter at dashboard level)
        apiPost('topic/dimension/all', { topic_id: topicId, dimension })
            .then(r => r.json())
            .then(result => {
                if (reqId !== requestIdRef.current) return; // stale
                const nps = result.nps || [];
                nps.sort((a, b) => b.count - a.count);
                setNpsData(nps);
            })
            .catch(err => {
                if (reqId !== requestIdRef.current) return;
                console.error('[useDimensionData] NPS fetch error:', err);
                setErrors(prev => ({ ...prev, nps: err }));
            })
            .finally(() => {
                if (reqId !== requestIdRef.current) return;
                setLoading(prev => ({ ...prev, nps: false }));
            });

        // Fetch quarterly data
        apiPost('topic/dimension/quarterly', { topic_id: topicId, dimension })
            .then(r => r.json())
            .then(result => {
                if (reqId !== requestIdRef.current) return;
                setQuarterlyData(result.data || []);
            })
            .catch(err => {
                if (reqId !== requestIdRef.current) return;
                console.error('[useDimensionData] quarterly fetch error:', err);
                setErrors(prev => ({ ...prev, quarterly: err }));
            })
            .finally(() => {
                if (reqId !== requestIdRef.current) return;
                setLoading(prev => ({ ...prev, quarterly: false }));
            });
    }, [topicId, dimension]);

    return { npsData, quarterlyData, loading, errors };
}
